import type { ReactNode } from 'react';
import { KpiCard, Delta, fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KapsamNotu } from './KapsamNotu';
import { MiniEgri } from './MiniEgri';
import { UrunKirilimiYok } from './UrunKirilimiYok';
import { pazaryeriSeciliMi, sepetHesaplanirMi, yemekKartiSeciliMi } from './kanal';
import { oranMetni, puanMetni } from './bicim';
import {
  fisOzeti, karOzeti, kopruNotu, kopruNotuMetni, magazaSepeti, sayi, yuzdeDegisim,
  type GunlukKarSatiri, type KanalSatiri, type SaKategoriSatiri,
} from './ozetHesaplari';

interface Props {
  kaynaklar: SatisKaynaklari;
  donem: Donem;
  oncekiDonem: Donem;
  sube?: string | null;
  kanal?: string | null;
}

/**
 * Delta rozeti ya da onun YERI. 🔴 CLS (2026-08-29, 390px Slow 4G olculdu): rozet sonradan belirince kart
 * +29px buyuyup altindakileri itiyordu (CLS 0,151). 21px = alt satirin tarayicida olculen yuksekligi.
 * K10: iki donem de GELDI ama kiyas yapilamiyorsa (onceki toplam yok/0 — ör. yillikta veri 06.02.2026'da
 * basliyor) rozet yerine "—"; ayni 21px yukseklik, sahte %0 ya da %∞ basilmaz.
 */
function Rozet({ deger, hazir }: { deger: number | null; hazir: boolean }) {
  if (!hazir) return <span className="block h-[21px]" aria-hidden="true" data-testid="rozet-yeri" />;
  if (deger === null)
    return (
      <span className="block h-[21px] text-ink-400" data-testid="delta-yok" title="Önceki dönemde karşılaştırılacak veri yok">
        —
      </span>
    );
  return <Delta value={deger} decimals={2} />;
}

function Kart({ testid, label, value, alt }: { testid: string; label: string; value: string; alt: ReactNode }) {
  return (
    <div data-testid={testid} className="min-w-0">
      {/* fmtTRY kendi ₺ isaretini basar; prefix="" cift sembolu onler. */}
      <KpiCard label={label} prefix="" value={value} sub={<div className="flex flex-col gap-1">{alt}</div>} />
    </div>
  );
}

/**
 * Satir 1 — dort kart (S256). Kaynaklar: 990040 bu + onceki (fis tabani; sube/kanal istemcide),
 * 990051 (urun tabani kar, marj, kopru), 990052 (kar egrisi). Iki taban + kopru: K2.
 */
export function OzetSerit({ kaynaklar, donem, oncekiDonem, sube = null, kanal = null }: Props) {
  // K10: TEK onceki pencere — 990040'in ikinci cagrisi ve 990051'in @ONCEKIBAS/@ONCEKIBIT'i ayni `oncekiDonem`.
  const f = { donem, sube, kanal, onceki: oncekiDonem };
  const urunYok = yemekKartiSeciliMi(kanal);
  // 040 TEK anahtar (final-fix F-3a): sube/kanal ASLA parametre olmaz, ds bildirse bile — istemcide suzulur;
  // KanalBlok/SubeBlok/ZamanBlok/useSubeler ayni anahtari paylasir (spec §4.4).
  const fisQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null });
  const fisOncekiQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem: oncekiDonem, sube: null, kanal: null });
  const kategoriQ = useSaSorgu<SaKategoriSatiri>(kaynaklar.kategori, f);
  // Kar egrisi yalniz kar kartinda: kart gizliyse (kategori kaynagi yok) 990052 de ACILMAZ.
  const karKartiVar = !!kaynaklar.kategori;
  const gunlukKarQ = useSaSorgu<GunlukKarSatiri>(kaynaklar.gunlukKar, f, !urunYok && karKartiVar);

  if (!kaynaklar.kanal040 && !kaynaklar.kategori) return null;

  const bu = fisQ.data ? fisOzeti(fisQ.data.data, sube, kanal) : null;
  const onceki = fisOncekiQ.data ? fisOzeti(fisOncekiQ.data.data, sube, kanal) : null;

  const sepetVar = sepetHesaplanirMi(kanal);
  const sepetBu = sepetVar && fisQ.data ? magazaSepeti(fisQ.data.data, sube) : null;
  const sepetOnceki = sepetVar && fisOncekiQ.data ? magazaSepeti(fisOncekiQ.data.data, sube) : null;
  const sepet = sepetBu ? sepetBu.sepet : null;
  const oncekiSepet = sepetOnceki ? sepetOnceki.sepet : null;

  const kar = kategoriQ.data ? karOzeti(kategoriQ.data.data) : null;
  const kopru = kategoriQ.data ? kopruNotu(kategoriQ.data.data) : null;
  const karEgrisi = [...(gunlukKarQ.data?.data ?? [])]
    .sort((a, b) => String(a.gun).localeCompare(String(b.gun)))
    .map((r) => sayi(r.kar));

  return (
    // Telefonda 2 kolon (dort kartta 2x2). Kar karti gizliyse genis ekranda 3 kolon: dorduncu kolon bos kalmasin.
    <div data-testid="blok-ozet" className={`grid grid-cols-2 ${karKartiVar ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
      <Kart
        testid="olcu-ciro"
        label="Net ciro"
        value={bu ? fmtTRY(bu.ciro) : '—'}
        alt={
          <>
            <Rozet hazir={!!bu && !!onceki} deger={bu && onceki ? yuzdeDegisim(bu.ciro, onceki.ciro) : null} />
            <MiniEgri testid="egri-ciro" veri={bu ? bu.gunluk.map((g) => g.ciro) : []} />
            {kopru && (
              <span data-testid="kopru-notu" className="text-[11px] text-ink-500">
                {kopruNotuMetni(kopru)}
              </span>
            )}
          </>
        }
      />
      <Kart
        testid="olcu-fis"
        label="Fiş"
        value={bu ? fmtNum(bu.fis) : '—'}
        alt={
          <>
            <Rozet hazir={!!bu && !!onceki} deger={bu && onceki ? yuzdeDegisim(bu.fis, onceki.fis) : null} />
            <MiniEgri testid="egri-fis" veri={bu ? bu.gunluk.map((g) => g.fis) : []} />
          </>
        }
      />
      <Kart
        testid="olcu-sepet"
        label="Sepet ortalaması"
        value={sepet !== null ? fmtTRY(sepet) : '—'}
        alt={
          sepetVar ? (
            <>
              <Rozet hazir={!!sepetBu && !!sepetOnceki} deger={sepet !== null ? yuzdeDegisim(sepet, oncekiSepet) : null} />
              <MiniEgri testid="egri-sepet" veri={sepetBu ? sepetBu.gunluk : []} />
              <span>Mağaza kanalı; kurumsal ve yemek kartı faturaları hariç</span>
            </>
          ) : (
            <span data-testid="sepet-kanal-notu">Sepet ortalaması yalnız Mağaza kanalında hesaplanır</span>
          )
        }
      />
      {/* Spec §5: kaynagi olmayan blok gizlenir — kar karti bos "—" ile yer tutmaz. */}
      {karKartiVar && (
        <Kart
          testid="olcu-kar"
          label="Brüt kâr"
          value={!urunYok && kar ? fmtTRY(kar.kar) : '—'}
          alt={
            urunYok ? (
              <UrunKirilimiYok />
            ) : (
              <>
                <Rozet hazir={kategoriQ.data !== undefined} deger={kar ? yuzdeDegisim(kar.kar, kar.oncekiKar) : null} />
                <MiniEgri testid="egri-kar" veri={karEgrisi} />
                {kar && kar.marj !== null && (
                  <span data-testid="olcu-marj">
                    Marj {oranMetni(kar.marj)}
                    {kar.oncekiMarj !== null && <> · {puanMetni(kar.marj - kar.oncekiMarj)}</>}
                  </span>
                )}
                {/* G1'den tasinan zorunlu not: komisyon ERP'de yok. */}
                {pazaryeriSeciliMi(kanal) && (
                  <span data-testid="komisyon-notu" className="text-warn-700">Pazaryeri komisyonu düşülmedi</span>
                )}
                <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.kategori, '@LOKASYON')} />
              </>
            )
          }
        />
      )}
    </div>
  );
}
